/**
 * Portfolio metric rating + circular progress helpers.
 * Thresholds aligned with Ratio Glossary (Figma 5626:88418).
 */

const COLORS = {
  poor: "#FF5252",
  low: "#17E9D9",
  average: "#17E9D9",
  moderate: "#17E9D9",
  elevated: "#FDDD60",
  good: "#45AC70",
  excellent: "#45AC70",
};

const TIER_PROGRESS = {
  poor: 18,
  low: 40,
  average: 48,
  moderate: 55,
  elevated: 48,
  good: 82,
  excellent: 95,
};

const METRIC_INFO = {
  sharpe:
    "The Sharpe Ratio evaluates how well an investment performs relative to its risk. Higher values indicate better risk-adjusted returns.",
  calmar:
    "The Calmar Ratio compares annualized returns with maximum drawdown. Higher values indicate better return-to-risk balance.",
  volatility:
    "Annual volatility shows how much a portfolio's returns fluctuate over a year. Higher volatility means more ups and downs.",
  sortino:
    "The Sortino Ratio measures returns relative to downside risk only. Higher values mean better downside risk-adjusted performance.",
  var: "Daily Value at Risk estimates the maximum potential loss in a single day under normal market conditions.",
};

const FALLBACK_COPY = {
  sharpe: {
    poor: "risk-adjusted returns are weak versus volatility taken",
    low: "return per unit of risk is below average",
    moderate: "acceptable return per unit of total volatility",
    good: "solid risk-adjusted performance versus volatility",
    excellent: "excellent return per unit of risk taken",
  },
  calmar: {
    poor: "inadequate return for the drawdown risk taken",
    average: "average return relative to maximum drawdown",
    low: "return relative to max drawdown is underwhelming",
    moderate: "acceptable recovery vs drawdowns; room to improve protection",
    good: "returns compensate well relative to max drawdown",
    excellent: "strong return relative to historical drawdowns",
  },
  volatility: {
    low: "Below Nifty 500 avg (~18%) — relatively controlled swings",
    moderate: "Near Nifty 500 avg (~18%). Balanced market risk profile",
    elevated: "Above Nifty 500 avg (~18%). Elevated return fluctuation",
    high: "Meaningfully above Nifty 500 avg (~18%); review concentration",
  },
  sortino: {
    poor: "downside volatility is not rewarded by returns",
    low: "downside risk control needs strengthening",
    moderate: "downside risk is only partially compensated",
    good: "downside risk is better controlled than total volatility suggests",
    excellent: "strong compensation for downside risk",
  },
  var: {
    poor: "expected short-term loss risk is elevated",
    low: "daily loss risk is elevated versus a balanced book",
    moderate: "daily loss risk is within a workable band",
    good: "downside risk is better controlled than total volatility suggests",
    excellent: "short-term loss risk is well contained",
  },
  beta: {
    low: "defensive — less sensitive than the Nifty 500 benchmark",
    moderate: "moves broadly in line with the Nifty 500 benchmark",
    elevated: "more volatile than the Nifty 500 benchmark",
    high: "meaningfully more volatile than the market",
  },
};

const normalizeKey = (key = "", label = "") => {
  const raw = `${key} ${label}`.toLowerCase();
  if (raw.includes("sharpe") || raw.includes("sharp_ratio") || /\bsharp\b/.test(raw)) {
    return "sharpe";
  }
  if (raw.includes("calmar") || raw.includes("calmer") || raw.includes("calmar_ration")) {
    return "calmar";
  }
  if (raw.includes("volatility") || raw.includes("vol")) return "volatility";
  if (raw.includes("sortino")) return "sortino";
  if (
    raw.includes("var") ||
    raw.includes("value at risk") ||
    raw.includes("daily_risk") ||
    raw.includes("daily risk")
  ) {
    return "var";
  }
  if (raw.includes("beta")) return "beta";
  if (raw.includes("drawdown") || raw.includes("mdd")) return "drawdown";
  if (raw.includes("tracking")) return "tracking";
  return "generic";
};

const normalizeThresholdTier = (threshold) => {
  if (threshold == null || threshold === "") return null;
  const raw = String(threshold).trim().toLowerCase();
  if (!raw) return null;
  if (raw.includes("excellent")) return "excellent";
  if (raw.includes("good")) return "good";
  if (raw.includes("moderate") || raw.includes("acceptable")) return "moderate";
  if (raw.includes("average") || raw.includes("below average")) return "low";
  if (raw.includes("elevated") || raw.includes("high")) return "elevated";
  if (raw.includes("poor") || raw.includes("weak") || raw.includes("low")) {
    if (raw.includes("low")) return "low";
    return "poor";
  }
  return null;
};

export const parseMetricNumber = (value) => {
  if (value == null || value === "-" || value === "") return null;
  const num = Number(String(value).replace(/%/g, "").replace(/,/g, "").trim());
  return Number.isFinite(num) ? num : null;
};

/**
 * Sharpe (glossary):
 * <0 poor | 0–0.5 below average | 0.5–1.0 acceptable | 1.0–2.0 good | >2.0 excellent
 */
const rateSharpe = (value) => {
  if (value == null) return "moderate";
  if (value < 0) return "poor";
  if (value < 0.5) return "low";
  if (value < 1.0) return "moderate";
  if (value < 2.0) return "good";
  return "excellent";
};

/**
 * Calmar (glossary):
 * <0.5 poor | 0.5–1.0 average | >1.0 good | >3.0 excellent
 */
const rateCalmar = (value) => {
  if (value == null) return "moderate";
  if (value < 0.5) return "poor";
  if (value < 1.0) return "average";
  if (value < 3.0) return "good";
  return "excellent";
};

/** Sortino — higher is better; same style bands as Sharpe-like ratios */
const rateSortino = (value) => {
  if (value == null) return "moderate";
  if (value < 0) return "poor";
  if (value < 0.75) return "low";
  if (value < 1.25) return "moderate";
  if (value < 2) return "good";
  return "excellent";
};

/** Linear progress for higher-is-better ratios (0 → max maps to 0–100). */
const linearProgress = (value, max, min = 0) => {
  if (value == null) return 0;
  const pct = ((value - min) / (max - min)) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
};

/**
 * Volatility vs Nifty 500 avg ~18% (glossary).
 * Progress rises with risk.
 */
const rateVolatility = (value) => {
  if (value == null) return "moderate";
  if (value < 15) return "low";
  if (value < 18) return "moderate";
  if (value < 22) return "elevated";
  return "high";
};

const rateVaR = (value) => {
  if (value == null) return "moderate";
  if (value <= 1) return "excellent";
  if (value <= 1.5) return "good";
  if (value <= 2.2) return "moderate";
  if (value <= 3) return "low";
  return "poor";
};

/** Beta: 1.0 = market; <1 defensive; >1 more volatile */
const rateBeta = (value) => {
  if (value == null) return "moderate";
  if (value < 0.85) return "low";
  if (value <= 1.05) return "moderate";
  if (value <= 1.25) return "elevated";
  return "high";
};

/**
 * MDD: smaller (less negative / closer to 0) is better.
 * Values may arrive as -18.4 or 18.4.
 */
const rateDrawdown = (value) => {
  if (value == null) return "moderate";
  const abs = Math.abs(value);
  if (abs <= 10) return "excellent";
  if (abs <= 15) return "good";
  if (abs <= 20) return "moderate";
  if (abs <= 30) return "low";
  return "poor";
};

const statusLabel = (tier, type) => {
  // Sharpe glossary wording
  if (type === "sharpe") {
    if (tier === "poor") return "Poor";
    if (tier === "low") return "Below Average";
    if (tier === "moderate") return "Moderate";
    if (tier === "good") return "Good";
    if (tier === "excellent") return "Excellent";
  }

  if (tier === "high" || tier === "elevated") return "Elevated";
  if (tier === "average") return "Average";
  if (tier === "low") return "Low";
  if (tier === "moderate") return "Moderate";
  return tier.charAt(0).toUpperCase() + tier.slice(1);
};

/**
 * @param {{ key?: string, label?: string, value?: string|number, tooltip?: string, threshold?: string }} metric
 */
export const getMetricInsight = (metric = {}) => {
  const type = normalizeKey(metric.key, metric.label);
  const numeric = parseMetricNumber(metric.value);
  let tier = "moderate";
  let progress = 50;

  switch (type) {
    case "sharpe":
      tier = rateSharpe(numeric);
      progress = linearProgress(numeric, 2, -0.5);
      break;
    case "calmar":
      tier = rateCalmar(numeric);
      progress = linearProgress(numeric, 3);
      break;
    case "sortino":
      tier = rateSortino(numeric);
      progress = linearProgress(numeric, 2, -0.5);
      break;
    case "volatility":
      tier = rateVolatility(numeric);
      progress = linearProgress(numeric, 30);
      break;
    case "var":
      tier = rateVaR(numeric);
      progress =
        numeric == null
          ? 50
          : Math.max(0, Math.min(100, Math.round(100 - (numeric / 4) * 100)));
      break;
    case "beta":
      tier = rateBeta(numeric);
      progress = linearProgress(Math.abs((numeric ?? 1) - 1), 0.5);
      break;
    case "drawdown":
      tier = rateDrawdown(numeric);
      progress =
        numeric == null
          ? 50
          : Math.max(
              0,
              Math.min(100, Math.round(100 - (Math.abs(numeric) / 40) * 100)),
            );
      break;
    default:
      tier = "moderate";
      progress = 50;
  }

  // Prefer backend-provided threshold when present
  const apiTier = normalizeThresholdTier(metric.threshold);
  if (apiTier) tier = apiTier;

  const tierProgress = TIER_PROGRESS[tier] ?? progress;
  const blended = Math.round(progress * 0.6 + tierProgress * 0.4);

  const color = COLORS[tier] || COLORS.moderate;
  const label = apiTier
    ? String(metric.threshold).trim()
    : statusLabel(tier, type);
  const copyMap = FALLBACK_COPY[type] || {};
  const fallbackBody =
    metric.tooltip ||
    copyMap[tier] ||
    copyMap.moderate ||
    copyMap.average ||
    "";
  const body = String(fallbackBody).replace(
    new RegExp(`^${label}\\s*—\\s*`, "i"),
    "",
  );

  return {
    type,
    numeric,
    tier,
    statusLabel: label,
    statusColor: color,
    progress: Math.max(8, Math.min(100, blended)),
    description: body,
    tooltip: metric.tooltip || body,
    info: METRIC_INFO[type] || "",
  };
};
